import React, {
  useCallback,
  useMemo,
  useRef,
} from 'react';
import moment from 'moment';
import format from 'string-template';
import PropTypes from 'prop-types';

import { DateFormat } from '../../../../utils/date';
import useLogger from '../../../../hooks/useLogger';
import WorkoutDayPanel from '../WorkoutDayPanel';
import {
  WorkoutHealthSummary,
  getMaxHeartRate,
  getWorkoutKeys,
  summarizeWeek,
  useWorkoutHealthData,
} from '../WorkoutHealth';
import { WorkoutFeedViewType } from '../utils';
import {
  Container,
  ItemContainer,
  StyledTitle,
  CountLabel,
  Description,
  StyledWorkoutProgress,
  DataContainer,
  WorkoutDetails,
} from './styles';
import texts from './texts.json';

const WorkoutStat = ({
  activity,
  clientAge,
}) => {
  const {
    workoutData: workoutList = [],
    checkInDay = 0,
    checkInId,
    user: clientId,
    coach: coachId,
  } = activity;
  const { logEvent } = useLogger();

  // Fires once per check-in instance the first time any progression badge renders.
  const hasLoggedProgressionRef = useRef(false);
  const handleProgressionShown = useCallback(() => {
    if (hasLoggedProgressionRef.current) return;
    hasLoggedProgressionRef.current = true;
    logEvent('progression_callout_shown', { check_in_id: checkInId, client_id: clientId });
  }, [checkInId, clientId, logEvent]);

  const dayAfterCheckIn = checkInDay + 1;
  const dayNames = useMemo(() => [
    ...moment.weekdays().slice(dayAfterCheckIn),
    ...moment.weekdays().slice(0, dayAfterCheckIn),
  ], [dayAfterCheckIn]);

  const maxHeartRate = getMaxHeartRate(clientAge);
  const { healthByWorkoutKey } = useWorkoutHealthData(clientId, coachId, workoutList);
  const workoutKeys = useMemo(() => getWorkoutKeys(workoutList), [workoutList]);
  const healthSummary = useMemo(() => summarizeWeek({
    workoutList,
    healthByWorkoutKey,
    workoutKeys,
    dayNames,
    maxHeartRate,
  }), [workoutList, healthByWorkoutKey, workoutKeys, dayNames, maxHeartRate]);

  const progressionByWorkoutTitle = useMemo(() => {
    const workoutProgressions = activity.progressionData?.workoutProgressions ?? [];
    return workoutProgressions.reduce((acc, { workoutTitle, workoutDate, exercises }) => {
      acc[`${(workoutTitle ?? '').trim()}|${workoutDate}`] = exercises;
      return acc;
    }, {});
  }, [activity]);
  // this will calculate an object with the user workouts grouped by type
  const workoutsByType = useMemo(() => {
    const initialObject = Object.keys(WorkoutFeedViewType).reduce((prev, type) => {
      const newObject = { ...prev };
      newObject[WorkoutFeedViewType[type]] = [];
      return newObject;
    }, {});

    return workoutList.reduce((prev, curr) => {
      const previous = { ...prev };
      previous[curr.type].push(curr);
      return previous;
    }, initialObject);
  }, [
    workoutList,
  ]);

  // Maps day name (e.g. "Monday") to its YYYY-MM-DD date string, derived from workout startDates.
  const dateByDayName = useMemo(() => {
    const map = {};
    workoutList.forEach((workout) => {
      // Key uses local weekday to match workoutsByDayAndType's grouping.
      // Value uses UTC date to match the server's workoutDate (which is also UTC).
      const weekDay = moment(workout.startDate.toDate()).format(DateFormat.WEEK_DAY_NAME);
      if (!map[weekDay]) {
        map[weekDay] = moment.utc(workout.startDate.toDate()).format('YYYY-MM-DD');
      }
    });
    return map;
  }, [workoutList]);

  // this will calculate an object with the user workouts grouped by day (which day of the week)
  // and type
  const workoutsByDayAndType = useMemo(() => {
    const initialObject = moment.weekdays().reduce((prev, current) => {
      const newObject = { ...prev };
      newObject[current] = {};
      Object.keys(WorkoutFeedViewType).forEach((type) => {
        newObject[current][WorkoutFeedViewType[type]] = [];
      });
      return newObject;
    }, {});

    return workoutList.reduce((prev, curr) => {
      const weekDay = moment(curr.startDate.toDate()).format(DateFormat.WEEK_DAY_NAME);
      const previous = { ...prev };
      previous[weekDay][curr.type].push(curr);
      return previous;
    }, initialObject);
  }, [
    workoutList,
  ]);

  const completedWorkoutCount = (workouts) => workouts
    .filter((workout) => !!workout.isCompleted)
    .length;

  const percentageCompleted = useMemo(() => (
    completedWorkoutCount(workoutsByType[WorkoutFeedViewType.COACH_ASSIGNED_WORKOUT])
    / workoutsByType[WorkoutFeedViewType.COACH_ASSIGNED_WORKOUT].length
  ), [
    workoutsByType,
  ]);

  const renderCompletedValue = useCallback((type) => {
    const count = completedWorkoutCount(workoutsByType[type]);
    let countLabel = count;
    if (count > 0 && count < 10) {
      countLabel = `0${count}`;
    }
    return countLabel;
  }, [
    workoutsByType,
  ]);

  return (
    <Container>
      <DataContainer>
        {/* coach asssigned workout summary */}
        <ItemContainer>
          <StyledTitle>{texts.workouts}</StyledTitle>
          <CountLabel>{renderCompletedValue(WorkoutFeedViewType.COACH_ASSIGNED_WORKOUT)}</CountLabel>
          <Description>
            {format(texts.goal, {
              goalCount: workoutsByType[WorkoutFeedViewType.COACH_ASSIGNED_WORKOUT].length,
            })}
          </Description>
          <StyledWorkoutProgress
            type="determinate"
            value={percentageCompleted || 0}
          />
        </ItemContainer>
        {/* custom workout summary */}
        <ItemContainer>
          <StyledTitle>{texts.customActivities}</StyledTitle>
          <CountLabel>{renderCompletedValue(WorkoutFeedViewType.CUSTOM_WORKOUT)}</CountLabel>
          <Description>{texts.logged}</Description>
        </ItemContainer>
      </DataContainer>
      {/* the week's health data, when any workout has it */}
      {healthSummary.syncedCount > 0 && (
        <WorkoutHealthSummary
          summary={healthSummary}
          maxHeartRate={maxHeartRate}
          isMaxHeartRateEstimated={!clientAge}
        />
      )}
      {/* expanded workout details for the each day of the week */}
      <WorkoutDetails>
        {dayNames.map((dayName) => (
          <WorkoutDayPanel
            key={dayName}
            dayName={dayName}
            workoutList={workoutsByDayAndType[dayName]}
            progressionByWorkoutTitle={progressionByWorkoutTitle}
            dayDate={dateByDayName[dayName] ?? ''}
            onProgressionShown={handleProgressionShown}
            healthByWorkoutKey={healthByWorkoutKey}
            workoutKeys={workoutKeys}
            maxHeartRate={maxHeartRate}
          />
        ))}
      </WorkoutDetails>
    </Container>
  );
};

WorkoutStat.propTypes = {
  activity: PropTypes.object,
  clientAge: PropTypes.number,
};

WorkoutStat.defaultProps = {
  activity: {},
  clientAge: null,
};

export default WorkoutStat;
